import React, { useState, useEffect, useRef } from 'react';
import { db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { Users, UserPlus, Search, User, ChevronLeft, ChevronRight, Send, X, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';

const FriendsTabDesktop = ({ friends, onAddFriend }) => {
  const [friendsWithDetails, setFriendsWithDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddFriendDropdown, setShowAddFriendDropdown] = useState(false);
  const [friendHandle, setFriendHandle] = useState('');
  const [sendingRequest, setSendingRequest] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [requestSuccess, setRequestSuccess] = useState(false);
  const friendsRowRef = useRef(null);
  const addFriendDropdownRef = useRef(null);
  const navigate = useNavigate();
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (addFriendDropdownRef.current && !addFriendDropdownRef.current.contains(event.target)) {
        setShowAddFriendDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Reset states when dropdown closes
  useEffect(() => {
    if (!showAddFriendDropdown) {
      setFriendHandle('');
      setRequestError('');
      setRequestSuccess(false);
    }
  }, [showAddFriendDropdown]);
  
  const handleSendFriendRequest = async () => {
    if (!friendHandle.trim()) {
      setRequestError('Please enter a handle');
      return;
    }
    
    try {
      setSendingRequest(true);
      setRequestError('');
      
      // Call the Firebase function
      const functions = getFunctions();
      const sendFriendRequest = httpsCallable(functions, 'sendFriendRequest');
      
      const result = await sendFriendRequest({
        receiverHandle: friendHandle.trim()
      });
      
      console.log('Friend request sent:', result.data);
      setRequestSuccess(true);
      setFriendHandle('');
      
      // Close dropdown after a short delay
      setTimeout(() => {
        setShowAddFriendDropdown(false);
      }, 2000);
      
    } catch (error) {
      console.error('Error sending friend request:', error);
      setRequestError(error.message || 'Failed to send friend request');
    } finally {
      setSendingRequest(false);
    }
  };
  
  useEffect(() => {
    const fetchFriendDetails = async () => {
      if (!friends || friends.length === 0) {
        setFriendsWithDetails([]);
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const detailedFriends = await Promise.all(
          friends.map(async (friend) => {
            // Get friend's user document for photo if not in friend document
            const friendId = friend.friendUserId;
            const friendDoc = await getDoc(doc(db, 'users', friendId));
            
            return {
              ...friend,
              id: friendId,
              name: friend.friendName || (friendDoc.exists() ? friendDoc.data().name : 'Unknown User'),
              handle: friend.friendHandle || (friendDoc.exists() ? friendDoc.data().handle : 'unknown'),
              photoURL: friendDoc.exists() ? friendDoc.data().photoURL : null,
              addedAt: friend.dateFriended || new Date()
            };
          })
        );
        
        setFriendsWithDetails(detailedFriends);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching friend details:', error);
        setLoading(false);
      }
    };
    
    fetchFriendDetails();
  }, [friends]);
  
  const filteredFriends = searchQuery 
    ? friendsWithDetails.filter(friend => 
        friend.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        friend.handle.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : friendsWithDetails;
  
  const scrollLeft = () => {
    if (friendsRowRef.current) {
      friendsRowRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };
  
  const scrollRight = () => {
    if (friendsRowRef.current) {
      friendsRowRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };
  
  const formatDate = (date) => {
    if (!date) return '';
    
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString();
  };
  
  return (
    <div style={{ width: '100%', color: 'white', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '1.5rem'
      }}>
        <h2 style={{ margin: 0, fontSize: '1.5rem' }}>Friends</h2>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: 'rgba(255,255,255,0.1)',
          borderRadius: '0.5rem',
          padding: '0 1rem',
          marginLeft: 'auto',
          maxWidth: '500px'
        }}>
          <Search size={18} color="#999" />
          <input
            type="text"
            value={searchQuery}
            
                className="search-input"
           
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search friends..."
            style={{
              flex: 1,
              padding: '0.5rem 0.5rem',
              backgroundColor: 'transparent',
              color: 'white',
              border: 'none',
              outline: 'none',
              fontSize: '0.9rem'
            }}
          />
        </div>
        
        {/* Add Friend Button with Dropdown */}
        <div style={{ position: 'relative' }} ref={addFriendDropdownRef}>
          <button
            onClick={() => setShowAddFriendDropdown(!showAddFriendDropdown)}
            style={{
              backgroundColor: 'rgb(255,255,255,.8)',
              color: '#6600cc',
              border: 'none',
              borderRadius: '0.5rem',
              marginLeft: '1rem',
              padding: '0.5rem 1rem',
              display: 'flex',
              fontWeight: '600',
              alignItems: 'center',
              gap: '0.5rem',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            <UserPlus size={18} />
            Add Friend
          </button>
          
          {/* Add Friend Dropdown */}
          {showAddFriendDropdown && (
            <div style={{
              position: 'absolute',
              top: '.5rem',
              right: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              borderRadius: '1rem',
              padding: '1rem',
              width: '300px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              zIndex: 100
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h3 style={{ margin: 0, fontSize: '1rem' }}>Send Friend Request To:</h3>
                <button 
                  onClick={() => setShowAddFriendDropdown(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    padding: '0.25rem'
                  }}
                >
                  <X size={16} />
                </button>
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <div style={{ 
                    flex: 1, 
                    display: 'flex', 
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    borderRadius: '0.25rem',
                    overflow: 'hidden'
                  }}>
                    <div style={{ 
                      padding: '0.5rem 0 0.5rem 0.5rem', 
                      color: '#999',
                      fontSize: '0.9rem',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      @
                    </div>
                    <input
                      type="text"
                      value={friendHandle}
                      onChange={(e) => setFriendHandle(e.target.value)}
                      placeholder="username"
                      style={{
                        flex: 1,
                        padding: '0.5rem',
                        backgroundColor: 'transparent',
                        color: 'white',
                        border: 'none',
                        outline: 'none',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                  <button
                    onClick={handleSendFriendRequest}
                    disabled={sendingRequest || !friendHandle.trim()}
                    style={{
                      backgroundColor: '#6600CC',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.25rem',
                      padding: '0.5rem',
                      cursor: sendingRequest || !friendHandle.trim() ? 'not-allowed' : 'pointer',
                      opacity: sendingRequest || !friendHandle.trim() ? 0.4 : 1
                    }}
                  >
                    {sendingRequest ? '...' : <Send size={16} style={{marginTop: '2px'}} />}
                  </button>
                </div>
              </div>
              
              {requestError && (
                <div style={{ 
                  color: '#ff6b6b', 
                  fontSize: '0.85rem', 
                  marginTop: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <AlertCircle size={14} />
                  {requestError}
                </div>
              )}
              
              {requestSuccess && (
                <div style={{ 
                  color: '#4BB543', 
                  fontSize: '0.85rem', 
                  marginTop: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  Friend request sent successfully!
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
          Loading friends...
        </div>
      ) : friendsWithDetails.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
          <Users size={48} style={{ opacity: 0.5, marginBottom: '1rem' }} />
          <p>You haven't added any friends yet</p>
          <button
            onClick={onAddFriend}
            style={{
              backgroundColor: 'rgb(255,255,255,.8)',
              color: '#6600cc',
              border: 'none',
              borderRadius: '0.5rem',
              marginLeft: '1rem',
              padding: '0.5rem 1rem',
              display: 'flex',
              fontWeight: '600',
              alignItems: 'center',
              gap: '0.5rem',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            <UserPlus size={18} />
            Add Friend
          </button>
        </div>
      ) : filteredFriends.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
          No friends match your search
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          {/* Left scroll button */}
          <button 
            onClick={scrollLeft}
            style={{
              position: 'absolute',
              left: '-20px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: 'rgba(0,0,0,0.7)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <ChevronLeft size={24} color="white" />
          </button>
          
          {/* Friends row */}
          <div 
            ref={friendsRowRef}
            style={{ 
              display: 'flex',
              overflowX: 'auto',
              scrollbarWidth: 'none', // Firefox
              msOverflowStyle: 'none', // IE/Edge
              gap: '1rem',
              padding: '0.5rem 0',
              scrollBehavior: 'smooth',
              '::-webkit-scrollbar': { display: 'none' } // Chrome/Safari/Opera
            }}
          >
            {filteredFriends.map((friend, index) => (
              <div
                key={friend.id}
                className="tab-item"
                style={{
                  '--item-index': index,
                  backgroundColor: 'rgba(0,0,0,0.3)',
                  borderRadius: '1rem',
                  padding: '1rem',
                  width: '20rem',
                  transition: 'transform 0.2s'
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%'
                }}>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '0.25rem', width: '16rem' }}>
                      {friend.name}
                    </div>
                    <div style={{display: 'flex'}}>
                      <div style={{ fontSize: '0.875rem', color: '#999' }}>
                        @{friend.handle}
                      </div>
                      <div style={{
                        fontSize: '0.75rem',
                        marginLeft: 'auto',
                        color: '#777',
                        whiteSpace: 'nowrap'
                      }}>
                        Added {formatDate(friend.dateFriended)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Right scroll button */}
          <button 
            onClick={scrollRight}
            style={{
              position: 'absolute',
              right: '-20px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: 'rgba(0,0,0,0.7)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <ChevronRight size={24} color="white" />
          </button>
        </div>
      )}
    </div>
  );
};

export default FriendsTabDesktop; 